import { SEGMENTED_BUTTON, SEGMENTED_GROUP } from '@src/components/ui/segmented';
import {
  type ComputeNode,
  CredentialsSubview,
  type NodeAgent,
  TypeId,
  dataContext,
  ExecutionEnvironmentStatus,
  PageId,
  ViewType,
} from '@sdk';
import { useAuth } from '@sdk/react/hooks';
import { Tooltip, TooltipContent, TooltipTrigger } from '@src/components/ui/tooltip';
import { useDockNavigation } from '@src/navigation/useDockNavigation';
import { credentialsPointer } from '@src/components/credentials-view/credentials-pointer';
import { useContext } from '@src/hooks/useContext';
import { useProjects } from '@src/hooks/use-projects';
import { ProjectActionsRow } from '@src/components/open-project-component/project-actions-row';
import { DesktopTile, TileSection } from '@src/components/quick-create/QuickCreatePanel';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@src/components/ui/dialog';
import {
  useSandboxes,
  isLaunched,
  isUserMachine,
  nextSandboxName,
  type SandboxDetails,
} from '@src/hooks/use-sandboxes';
import { StepList } from '@src/components/ui/step-list';
import { NewSandboxDialog } from './NewSandboxDialog';
import { LaunchSandboxDialog } from './LaunchSandboxDialog';
import { ConnectMachineDialog } from './ConnectMachineDialog';
import { ShareSandboxDialog } from './ShareSandboxDialog';
import { LoginAccessDialog } from './LoginAccessDialog';
import { AddMachineDialog } from '@src/components/hub/AddMachineDialog';
import { HubItemsSection } from './HubItemsSection';
import { hubProjectAssetDock } from '@src/lib/hub-page-url';
import { FirstAgentCard } from './first-agent/FirstAgentCard';
import { SharedProjectsBanner, sharedProjectsToOpen } from './SharedProjectsBanner';
import { ConfirmDialog } from '@src/components/ui/confirm-dialog';
import { BulkConfirmDialog } from '@src/components/ui/bulk-confirm-dialog';
import { Checkbox } from '@src/components/ui/checkbox';
import { type BulkAction, BULK_ACTIONS, bulkActionCopy, bulkTargets, canUpgrade } from './machines-bulk';
import { isDisconnected, isOperable } from './machine-status';
import { MembershipInvitations } from '@src/components/stream-inbox-view/MembershipInvitations';
import {
  ArrowUpCircle,
  KeyRound,
  Laptop,
  Loader2,
  LogOut,
  Monitor,
  Plug,
  Plus,
  ShieldCheck,
  Trash2,
  UserPlus,
  X,
  Bot,
} from 'lucide-react';
import { Button } from '@src/components/ui/button';
import { Trans, useLingui } from '@lingui/react/macro';
import { consumeInboundParams } from '@src/navigation/inbound-link';
import { useEffect, useMemo, useRef, useState } from 'react';
import { isSignedInAsMe } from './signed-in-as-me';
import { useUiActionRequest } from '@src/navigation/ui-actions';

// Live sandbox status styling, keyed off the backend `ExecutionEnvironmentStatus`
// (`ops/status`). `card` tints the whole sandbox block so status reads at a glance.
const STATUS_STYLE: Record<ExecutionEnvironmentStatus, { dot: string; card: string }> = {
  [ExecutionEnvironmentStatus.READY]: { dot: 'bg-green-500', card: 'border-green-500/40 bg-green-500/5' },
  [ExecutionEnvironmentStatus.PAUSED]: { dot: 'bg-yellow-500', card: 'border-yellow-500/40 bg-yellow-500/5' },
  [ExecutionEnvironmentStatus.NOT_FOUND]: { dot: 'bg-muted-foreground/40', card: 'border-border opacity-60' },
  [ExecutionEnvironmentStatus.ERROR]: { dot: 'bg-destructive', card: 'border-destructive/40 bg-destructive/5' },
  [ExecutionEnvironmentStatus.NEW]: { dot: 'bg-muted-foreground/40', card: 'border-border' },
  [ExecutionEnvironmentStatus.DISCONNECTED]: { dot: 'bg-muted-foreground/40', card: 'border-border' },
};

/**
 * The glyph that says what kind of box a card is: a connected computer or a workspace. Anything
 * else — agent machines included — gets none: no field on a node marks it as an agent's (the hub
 * records that on the deployment), so any other glyph would be a guess.
 */
function CardGlyph({ node }: { node: ComputeNode }) {
  const { t } = useLingui();
  const cls = 'h-4 w-4 shrink-0 text-muted-foreground';
  if (isUserMachine(node))
    return <Laptop className={cls} aria-label={t`Connected computer`} data-testid="sandbox-kind-machine" />;
  if (node.isSandbox) return <Monitor className={cls} aria-label={t`Workspace`} data-testid="sandbox-kind-workspace" />;
  return null;
}

/** Border/background tint for a sandbox card, by live status. */
function statusCardClass(status?: ExecutionEnvironmentStatus): string {
  return (status && STATUS_STYLE[status]?.card) || 'border-border';
}

/** "12m", "1h 5m" — minutes granularity, clamped at 0. */
function fmtDur(ms: number): string {
  const m = Math.max(0, Math.floor(ms / 60000));
  return m < 60 ? `${m}m` : `${Math.floor(m / 60)}h ${m % 60}m`;
}

function fmtSize(cpu?: number, memMb?: number): string | null {
  if (!cpu || !memMb) return null;
  const mem = memMb >= 1024 ? `${(memMb / 1024).toFixed(memMb % 1024 ? 1 : 0)} GiB` : `${memMb} MiB`;
  return `${cpu} vCPU · ${mem}`;
}

/**
 * Second line of a sandbox card: dot + label + (for running) time used /
 * pauses-in + size, then who the box is signed in as.
 *
 * The sign-in half comes from the node itself (`logged_in_user`), which the hub
 * caches whenever it brings the workspace up — so it costs nothing to render and
 * does not wake a paused machine to ask. Before this you could only learn whose
 * session a shared box was running by opening the share dialog.
 */
function SandboxStatus({
  info,
  now,
  launched = true,
  launching = false,
  loggedInUser,
  autoLogin,
}: {
  info?: SandboxDetails;
  now: number;
  /** Has this box ever been booted? An unlaunched one has no status to probe. */
  launched?: boolean;
  /** Is it booting right now? */
  launching?: boolean;
  loggedInUser?: string | null;
  autoLogin?: boolean;
}) {
  const { t } = useLingui();
  // The login half comes from the ENTITY, not from the status probe, so it must
  // render even while the probe is outstanding — and even if it never lands. A
  // box whose status is unreachable is exactly when "who is this signed in as"
  // is worth reading.
  // Its OWN row, not appended to the status line. Sharing a row meant competing
  // for width with "Running · 12m used · pauses in 3h · 2 vCPU · 2 GiB" inside a
  // `truncate`, and the sign-in — the half you cannot get anywhere else on this
  // page — was the part that disappeared.
  const login = (
    <span className="flex min-w-0 items-center gap-1.5 ps-7 text-[11px] text-muted-foreground">
      <LoginLine loggedInUser={loggedInUser} autoLogin={autoLogin} />
    </span>
  );
  // Never launched: there is no machine to have a status. Saying "Checking…"
  // here would be a probe that is never coming, and "Unreachable" would blame a
  // box that was never built.
  if (!launched) {
    return (
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="flex items-center gap-1.5 ps-7 text-[11px] text-muted-foreground/50">
          <span
            className={`h-2 w-2 shrink-0 rounded-full bg-muted-foreground/40 ${launching ? 'animate-pulse' : ''}`}
          />
          <span data-testid="sandbox-not-launched">{launching ? t`Starting…` : t`Not started`}</span>
        </span>
      </div>
    );
  }
  if (!info) {
    return (
      <div className="flex min-w-0 flex-col gap-0.5">
        <span className="flex items-center gap-1.5 ps-7 text-[11px] text-muted-foreground/50">
          <span className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-muted-foreground/40" />
          {t`Checking…`}
        </span>
        {login}
      </div>
    );
  }
  const status = info.status;
  const labels: Record<ExecutionEnvironmentStatus, string> = {
    [ExecutionEnvironmentStatus.READY]: t`Running`,
    [ExecutionEnvironmentStatus.PAUSED]: t`Paused`,
    [ExecutionEnvironmentStatus.NOT_FOUND]: t`Not found`,
    [ExecutionEnvironmentStatus.ERROR]: t`Unreachable`,
    [ExecutionEnvironmentStatus.NEW]: t`New`,
    [ExecutionEnvironmentStatus.DISCONNECTED]: t`Disconnected`,
  };
  const startedAt = info.started_at ? new Date(info.started_at).getTime() : null;
  const parts: string[] = [];
  if (status === ExecutionEnvironmentStatus.READY) {
    if (info.end_at) parts.push(`pauses in ${fmtDur(new Date(info.end_at).getTime() - now)}`);
  }
  const size = fmtSize(info.cpu_count, info.memory_mb);
  if (size) parts.push(size);
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span
        className="flex min-w-0 items-center gap-1.5 ps-7 text-[11px] text-muted-foreground"
        title={info.status_msg || status}
      >
        <span className={`h-2 w-2 shrink-0 rounded-full ${STATUS_STYLE[status]?.dot ?? 'bg-muted-foreground/40'}`} />
        <span className="shrink-0" data-testid="sandbox-status-label">
          {labels[status] ?? status}
        </span>
        {parts.length > 0 && <span className="truncate text-muted-foreground/70">· {parts.join(' · ')}</span>}
      </span>
      {startedAt !== null && (
        <span className="truncate ps-7 text-[11px] text-muted-foreground/70" data-testid="sandbox-started">
          {t`started ${new Date(startedAt).toLocaleString()}`}
          {status === ExecutionEnvironmentStatus.READY && ` · ${t`running ${fmtDur(now - startedAt)}`}`}
        </span>
      )}
      {login}
    </div>
  );
}

/** The agents placed on this machine, one icon each; a click opens that agent in its project's editor. */
function NodeAgents({ node }: { node: ComputeNode }) {
  const { t } = useLingui();
  const { navigation } = useDockNavigation();
  const [agents, setAgents] = useState<NodeAgent[]>([]);
  // Keyed by the machine's id: the card's node object is rebuilt on every list refresh.
  const nodeRef = useRef(node);
  nodeRef.current = node;
  useEffect(() => {
    let live = true;
    nodeRef.current
      .agents()
      .then((rows) => live && rows.length > 0 && setAgents(rows))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [node.id]);
  if (agents.length === 0) return null;
  return (
    <span className="flex min-w-0 flex-wrap items-center gap-1 ps-7" data-testid="machine-agents">
      {agents.map((a) => (
        <Tooltip key={a.deployment_typeid}>
          <TooltipTrigger asChild>
            <button
              type="button"
              disabled={!a.project_id}
              onClick={() => navigation.openDock(hubProjectAssetDock(a.project_id, new TypeId(a.typeid)))}
              className="flex max-w-[12rem] items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] hover:bg-muted"
              data-testid={`machine-agent-${a.typeid}`}
            >
              <Bot className="h-3 w-3 shrink-0" />
              <span className="truncate">{a.name || t`Agent`}</span>
            </button>
          </TooltipTrigger>
          <TooltipContent>{t`Open the agent`}</TooltipContent>
        </Tooltip>
      ))}
    </span>
  );
}

/** Who the box is signed in as, and whether it still signs itself in. */
function LoginLine({ loggedInUser, autoLogin }: { loggedInUser?: string | null; autoLogin?: boolean }) {
  const { t } = useLingui();
  return (
    <>
      {/* `null` covers both "signed out" and "never looked" — indistinguishable
          from here, and the honest rendering of both is the same. */}
      {/* No leading separator any more: this renders on its own row, where a
          dangling "·" reads as a missing first item rather than a join. */}
      {loggedInUser ? (
        <span className="truncate text-muted-foreground/70" data-testid="sandbox-user">
          {t`signed in as ${loggedInUser}`}
        </span>
      ) : (
        <span className="shrink-0 text-muted-foreground/50" data-testid="sandbox-user-none">
          {t`signed out`}
        </span>
      )}
      {autoLogin === false && (
        <span className="shrink-0 text-muted-foreground/50" data-testid="sandbox-auto-login-off">
          · {t`auto-login off`}
        </span>
      )}
    </>
  );
}

const BULK_ICON: Record<BulkAction, typeof Trash2> = { upgrade: ArrowUpCircle, signout: LogOut, delete: Trash2 };

/** The one confirm every bulk action goes through: what will run, and how many are skipped. */
function BulkActionConfirm({
  action,
  targets,
  skipped,
  onClose,
  onConfirm,
}: {
  action: BulkAction;
  targets: ComputeNode[];
  skipped: number;
  onClose: () => void;
  onConfirm: (nodes: ComputeNode[]) => void;
}) {
  const copy = bulkActionCopy(action);
  const destructive = action === 'delete';
  return (
    <BulkConfirmDialog
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={copy.title(targets.length)}
      intro={skipped > 0 ? `${copy.intro} ${copy.skipped(skipped)}` : copy.intro}
      buckets={[{ label: copy.will, count: targets.length, tone: destructive ? 'destructive' : 'default' }]}
      confirmLabel={copy.confirm}
      variant={destructive ? 'destructive' : 'default'}
      onConfirm={() => onConfirm(targets)}
    />
  );
}

/**
 * HubHome — the hub page's landing. Mirrors the desktop app HOME (`HomeLanding`)
 * look (centered greeting + a hero band + cards) but uses ONLY hub-served data
 * (projects plus the shared WorldView API). No desktop-only
 * surfaces (stream inbox/feed/scan/vibe-session), so nothing 404/422s against the hub.
 *
 * URL: /dock/hub/home  (page=hub, viewType=home → routed here by ContentPanel).
 */
/** `machines` renders only the Machines page (/dock/hub/machines); the default is the home. */
export function HubHome({ view = 'home' }: { view?: 'home' | 'machines' }) {
  const showMachines = view === 'machines';
  const { t } = useLingui();
  const { currentUser } = useAuth();
  const { navigation } = useDockNavigation();

  /** Open the Credentials view.
   *
   *  Navigation and nothing else — no context writes, per the URL-first rule.
   *  `openPage` and not `openDock`: this card lives on the hub page, and a
   *  page-less pointer is redirected straight back to /dock/hub/home by
   *  `pageRedirectUrl` on a hub-only server.
   *
   *  Deliberately does NOT attach anything inline: these cards drive a HUB
   *  backend, which does not have the attach actions, so the panel shows its
   *  own empty state there rather than this button pretending to work. */
  const openSandboxSecrets = () => {
    navigation.openPage(PageId.HUB, ViewType.CREDENTIALS, credentialsPointer(CredentialsSubview.CONNECTIONS));
  };
  // Current project is the same source the footer's StatusBar reads
  // (dataContext.project), so the highlighted card and the footer always agree.
  const { project: currentProject } = useContext();
  const { projects } = useProjects({ priority: 'demand' });
  const sharedProjects = useMemo(() => sharedProjectsToOpen(projects, currentUser?.id), [projects, currentUser?.id]);
  const {
    sandboxes,
    createSandbox,
    launchSandbox,
    launchingId,
    creating,
    steps,
    openSandbox,
    renameSandbox,
    deleteSandbox,
    deletingIds,
    logoutSandbox,
    loggingOutIds,
    upgradeSandbox,
    upgradingIds,
    runBulkAction,
    details,
    refetch,
  } = useSandboxes({ pollMachines: showMachines });
  // Absent on older hubs that don't advertise the flag yet — treat as enabled.
  const sandboxesEnabled = dataContext.bootstrapInfo?.sandboxes_enabled !== false;
  // `sandboxes_enabled` says the hub can provision CLOUD sandboxes. A machine a
  // person connected with `flow connect` needs no provider, so its card stays
  // usable on a hub that has none — Open was dead on every connected machine there.
  const cardEnabled = (node: ComputeNode) => sandboxesEnabled || isUserMachine(node);
  // Creating a sandbox needs BOTH a provisioning-capable hub (e2b key) and a
  // signed-in user — a visitor's launch would just 401.
  const canCreateSandbox = sandboxesEnabled && !!currentUser;

  // Deleting a project. Held as the whole entity, not an id, so the confirm can
  // name what it is about to destroy.

  // Active hides machines the provider no longer has (Not found).
  const firstAgentRef = useRef<HTMLDivElement>(null);
  const [machineFilter, setMachineFilter] = useState<'active' | 'all'>('active');
  const activeSandboxes = sandboxes.filter((d) => details[d.id]?.status !== ExecutionEnvironmentStatus.NOT_FOUND);
  const shownSandboxes = machineFilter === 'all' ? sandboxes : activeSandboxes;

  // Multi-select. Only cards on screen count: `selectedNodes` is derived from
  // what is shown, so a filter change can never send a hidden card to a bulk action.
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(() => new Set());
  const selectedNodes = shownSandboxes.filter((d) => selectedIds.has(d.id));
  const selecting = selectedNodes.length > 0;
  const allSelected = selectedNodes.length === shownSandboxes.length;
  const toggleSelected = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const clearSelection = () => setSelectedIds(new Set());
  useEffect(() => {
    if (!selecting) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') clearSelection();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selecting]);
  const targets = bulkTargets(selectedNodes, details, currentUser?.email, cardEnabled);
  // The bulk action whose confirm is open, or null.
  const [bulkAction, setBulkAction] = useState<BulkAction | null>(null);
  // Inline rename: single-click a sandbox name to edit it.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');

  // Tick every 30s so the "used / pauses in" countdowns stay live without a reload.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  // New-sandbox modal (name + optional git). Opened by the New Sandbox card, or
  // pre-filled from a `?setup_git=<git-url>` deep link.
  // One state: null = closed. Open sites can't disagree about the prefill.
  const [newSandbox, setNewSandbox] = useState<{ gitUrl?: string } | null>(null);
  // The sandbox whose share dialog is open, or null. Holds the node itself so
  // the dialog can read `auto_login` without a second fetch.
  const [sharing, setSharing] = useState<ComputeNode | null>(null);
  const [editingLogin, setEditingLogin] = useState<ComputeNode | null>(null);
  // The never-launched sandbox whose launch dialog is open, or null. Launching
  // asks first because it is the click that starts costing money, and because
  // auto-login can only be chosen before the box signs anyone in. Opening an
  // already-launched box stays one click — it asks nothing and starts nothing.
  const [launching, setLaunching] = useState<ComputeNode | null>(null);
  // The disconnected machine whose Connect instructions are open, or null.
  const [connecting, setConnecting] = useState<ComputeNode | null>(null);
  // The sandbox whose upgrade confirm is open, or null. Asks first because the
  // upgrade STOPS the app inside the box: anyone working in it — the owner in
  // another tab, or someone they shared it with — is interrupted for as long as
  // the install and the boot take.
  const [upgrading, setUpgrading] = useState<ComputeNode | null>(null);
  // Drives the "accepting adds it below" hint, and lets the sandbox list
  // refresh once an invitation is accepted (the granted node appears in it).
  const [pendingInviteCount, setPendingInviteCount] = useState(0);
  const prevPendingInvites = useRef(0);
  useEffect(() => {
    // A count that DROPS means one was accepted or declined; on accept the
    // recipient now holds a role on the node, so the list below is stale.
    if (pendingInviteCount < prevPendingInvites.current) void refetch();
    prevPendingInvites.current = pendingInviteCount;
  }, [pendingInviteCount, refetch]);
  // `flow connect` device-code approval: null = closed, '' = open blank, 'XXXX-XXXX' = prefilled.
  const [addMachineCode, setAddMachineCode] = useState<string | null>(null);
  const [addMachineOpen, setAddMachineOpen] = useState(false);
  // "create a sandbox" / "add a machine" (smart navigation): the same dialogs the buttons open.
  useUiActionRequest(['new-sandbox-hub', 'add-machine-hub'], (id) =>
    id === 'new-sandbox-hub' ? setNewSandbox({}) : setAddMachineCode(''),
  );
  useEffect(() => {
    // Read-and-scrub in one call, so a refresh cannot re-open the dialog.
    const { setup_git: gitUrl, connect_code: connectCode } = consumeInboundParams(['setup_git', 'connect_code']);
    if (connectCode) setAddMachineCode(connectCode);
    if (!gitUrl) return;
    setNewSandbox({ gitUrl });
  }, []);

  const firstName = currentUser?.name?.split(' ')[0] || 'there';
  // Named outside the confirm's own message: a `t` macro cannot be nested inside
  // another one's interpolation, and the fallback needs translating too.
  const upgradeTargetName = upgrading?.name || t`this sandbox`;

  return (
    <div className="flex h-full flex-col overflow-auto">
      <div className="mx-auto flex w-full max-w-5xl flex-1 flex-col gap-8 px-4 py-10 sm:py-14">
        {!showMachines && (
          <>
            {/* Greeting — small, left-aligned; the counters live in the tabs below. */}
            <div className="flex flex-col items-start text-left">
              <h1 className="text-xl font-bold tracking-tight">
                <Trans>
                  Hey{' '}
                  <span className="bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
                    {firstName}
                  </span>
                </Trans>
              </h1>
            </div>

            {/* First agent: describe → its own sandbox + a channel → it answers. */}
            <div ref={firstAgentRef} className="w-full">
              <FirstAgentCard />
            </div>

            {/* Projects other people gave you — the way from here to FlowPad. */}
            <SharedProjectsBanner projects={sharedProjects} />

            {/* Projects — the Vibe home's project strip, reused as-is (ProjectActionsRow). */}
            <ProjectActionsRow className="w-full self-start" />

            {/* The counters as tabs over the items they count. */}
            <HubItemsSection
              onCreate={() => firstAgentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
            />
          </>
        )}

        {showMachines && (
          <>
            {/* Pending invitations.
            Lives here because the hub page has no Stream Inbox: `renderHubBody`
            (content-panel.tsx) has no ViewType.STREAM_INBOX case, so `StreamInboxView` — and
            with it the usual home for `MembershipInvitations` — never renders
            under page=hub. Without this the rows are fetchable and have nowhere
            to appear. Above Sandboxes deliberately: a sandbox share is the
            invitation this page will mostly receive, and accepting one changes
            the list directly below it. Renders nothing when there are none. */}
            <MembershipInvitations recipientEmail={currentUser?.email ?? null} onPendingCount={setPendingInviteCount} />
            {pendingInviteCount > 0 && (
              <p className="-mt-2 text-xs text-muted-foreground">
                <Trans>Accepting adds it to your Sandboxes below.</Trans>
              </p>
            )}

            {/* Machines — reached from the home page's Machines tab (HubItemsSection). */}
            <div className="flex flex-col gap-3">
              <h2 className="text-sm font-medium text-muted-foreground">
                <Trans>Machines</Trans>
              </h2>
              <DesktopTile
                Icon={Plus}
                label={creating ? t`Creating…` : t`Add machine`}
                loading={creating}
                onClick={() => setAddMachineOpen(true)}
                data-testid="add-machine-open"
              />
              <Dialog open={addMachineOpen} onOpenChange={setAddMachineOpen}>
                <DialogContent className="max-w-2xl" data-testid="add-machine-dialog">
                  <DialogHeader>
                    <DialogTitle>
                      <Trans>Add machine</Trans>
                    </DialogTitle>
                    <DialogDescription>
                      <Trans>Start a cloud sandbox, or connect a computer you own.</Trans>
                    </DialogDescription>
                  </DialogHeader>
                  <TileSection title={<Trans>Machine type</Trans>}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <DesktopTile
                          Icon={Monitor}
                          label={t`Sandbox`}
                          disabled={!canCreateSandbox}
                          onClick={() => {
                            setAddMachineOpen(false);
                            setNewSandbox({});
                          }}
                          data-testid="new-sandbox-button"
                        />
                      </TooltipTrigger>
                      {!canCreateSandbox && (
                        <TooltipContent>
                          {!sandboxesEnabled ? (
                            // An operator's missing provider config, not something the viewer can retry.
                            <Trans>This hub isn't configured to run sandboxes</Trans>
                          ) : (
                            <Trans>Sign in to create sandboxes</Trans>
                          )}
                        </TooltipContent>
                      )}
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <DesktopTile
                          Icon={Laptop}
                          label={t`Machine`}
                          disabled={!currentUser}
                          onClick={() => {
                            setAddMachineOpen(false);
                            setAddMachineCode('');
                          }}
                          data-testid="add-machine-button"
                        />
                      </TooltipTrigger>
                      <TooltipContent>
                        {currentUser ? (
                          <Trans>Run `flow connect` on a machine and enter its code here</Trans>
                        ) : (
                          <Trans>Sign in to add machines</Trans>
                        )}
                      </TooltipContent>
                    </Tooltip>
                  </TileSection>
                </DialogContent>
              </Dialog>

              {selecting ? (
                <div
                  className="flex min-h-9 flex-wrap items-center gap-3 rounded-md border bg-muted/40 px-3 py-1"
                  data-testid="machines-selection-bar"
                >
                  <Checkbox
                    checked={allSelected ? true : 'indeterminate'}
                    onCheckedChange={() =>
                      setSelectedIds(allSelected ? new Set() : new Set(shownSandboxes.map((d) => d.id)))
                    }
                    aria-label={t`Select all machines`}
                    data-testid="machines-select-all"
                  />
                  <span className="text-sm font-medium" data-testid="machines-selection-count">
                    {t`${selectedNodes.length} selected`}
                  </span>
                  {BULK_ACTIONS.map((action) => {
                    const Icon = BULK_ICON[action];
                    return (
                      <Button
                        key={action}
                        size="sm"
                        variant="secondary"
                        className={`h-7 px-2.5 text-xs ${action === 'delete' ? 'text-destructive' : ''}`}
                        disabled={!targets[action].length}
                        onClick={() => setBulkAction(action)}
                        data-testid={`machines-bulk-${action}`}
                      >
                        <Icon className="me-1.5 h-3 w-3" />
                        {bulkActionCopy(action).button(targets[action].length)}
                      </Button>
                    );
                  })}
                  <button
                    type="button"
                    onClick={clearSelection}
                    aria-label={t`Clear selection`}
                    className="ms-auto text-muted-foreground hover:text-foreground"
                    data-testid="machines-selection-clear"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <div role="radiogroup" className={`${SEGMENTED_GROUP} h-9 w-fit`} data-testid="machine-filter">
                  {(['active', 'all'] as const).map((f) => (
                    <button
                      key={f}
                      type="button"
                      role="radio"
                      aria-checked={machineFilter === f}
                      onClick={() => setMachineFilter(f)}
                      className={`${SEGMENTED_BUTTON} h-7 w-auto px-4 text-sm ${machineFilter === f ? 'bg-primary font-medium text-primary-foreground' : 'text-foreground/80 hover:bg-accent hover:text-foreground'}`}
                      data-testid={`machine-filter-${f}`}
                    >
                      {f === 'active' ? t`Active` : t`All`}{' '}
                      <span className="ms-1.5 opacity-70">
                        ({f === 'all' ? sandboxes.length : activeSandboxes.length})
                      </span>
                    </button>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {shownSandboxes.map((d) => (
                  <div
                    key={d.id}
                    data-testid="sandbox-card"
                    data-node-id={d.id}
                    data-provider-id={d.node_provider_id}
                    data-status={details[d.id]?.status}
                    data-selected={selectedIds.has(d.id) || undefined}
                    title={cardEnabled(d) ? undefined : t`Sandbox unavailable`}
                    // Ctrl/⌘-click anywhere on the card toggles it, like a file list.
                    onClickCapture={(e) => {
                      if (!(e.metaKey || e.ctrlKey)) return;
                      e.preventDefault();
                      e.stopPropagation();
                      toggleSelected(d.id);
                    }}
                    className={`group flex flex-col gap-1.5 rounded-lg border bg-card px-4 py-3 transition-colors ${statusCardClass(
                      details[d.id]?.status,
                    )} ${cardEnabled(d) ? '' : 'opacity-60'} ${selectedIds.has(d.id) ? 'ring-2 ring-primary' : ''}`}
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={selectedIds.has(d.id)}
                        onCheckedChange={() => toggleSelected(d.id)}
                        aria-label={t`Select ${d.name || 'machine'}`}
                        data-testid="sandbox-card-select"
                        className={`transition-opacity ${selecting ? 'opacity-100' : 'opacity-0 focus-visible:opacity-100 group-hover:opacity-100'}`}
                      />
                      <CardGlyph node={d} />
                      {editingId === d.id ? (
                        <input
                          autoFocus
                          value={draftName}
                          onChange={(e) => setDraftName(e.target.value)}
                          onBlur={() => {
                            void renameSandbox(d, draftName);
                            setEditingId(null);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') e.currentTarget.blur();
                            else if (e.key === 'Escape') setEditingId(null);
                          }}
                          data-testid="sandbox-name-input"
                          className="min-w-0 flex-1 border-b border-border bg-transparent text-sm outline-none"
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => {
                            setDraftName(d.name || '');
                            setEditingId(d.id);
                          }}
                          disabled={!cardEnabled(d)}
                          className="min-w-0 flex-1 truncate text-start text-sm font-semibold hover:underline disabled:pointer-events-none"
                          title={d.name || t`Sandbox`}
                          data-testid="sandbox-name"
                        >
                          {d.name || t`Sandbox`}
                        </button>
                      )}
                    </div>
                    {/* Actions get their own line under the title, so a long name
                    is not squeezed to a few letters by the buttons. */}
                    <div className="flex items-center gap-3">
                      {/* A labelled button, and visible WITHOUT hovering.
                      Opening is the one thing you come to this card to do, and
                      as a hover-only icon it was both undiscoverable and
                      indistinguishable from the share/secrets/delete icons
                      beside it. Those stay as hover icons — they are the rarer,
                      more destructive actions. */}
                      {/* Two different acts behind one slot. A box that was never
                      launched has no VM to open — the hub answers "this machine
                      has not been set up yet" — so offering "Open" would be a
                      button that 409s. Launch asks first; Open does not. */}
                      {/* And nothing at all while the probe says the box is
                      unreachable: there is a VM, so "Launch" is the wrong offer,
                      but opening it can only fail. The status line beside it
                      already says "Unreachable"; a button that reliably errors
                      would just be a second way to learn that. The slot comes
                      back on its own when the next poll finds the box. */}
                      {isLaunched(d)
                        ? isOperable(details[d.id]) && (
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => openSandbox(d)}
                              disabled={!cardEnabled(d)}
                              aria-label={t`Open sandbox`}
                              data-testid="sandbox-open"
                              className="h-7 shrink-0 px-2.5 text-xs"
                            >
                              <Trans>Open</Trans>
                            </Button>
                          )
                        : null}
                      {/* A connected machine's own shells: each click spins out one more
                      terminal session on it (the same path as the terminal strip). */}
                      {isDisconnected(details[d.id]) && (
                        <Button
                          size="sm"
                          onClick={() => setConnecting(d)}
                          aria-label={t`Connect this machine`}
                          data-testid="machine-connect"
                          className="h-7 shrink-0 px-2.5 text-xs"
                        >
                          <Plug className="me-1.5 h-3 w-3" />
                          <Trans>Connect</Trans>
                        </Button>
                      )}
                      {isUserMachine(d) && isOperable(details[d.id]) && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => void navigation.openNewShell({ computeNode: d })}
                          disabled={!cardEnabled(d)}
                          aria-label={t`Open a terminal on this machine`}
                          data-testid="machine-terminal"
                          className="h-7 shrink-0 px-2.5 text-xs"
                        >
                          <Trans>Terminal</Trans>
                        </Button>
                      )}
                      {isLaunched(d) ? null : (
                        <Button
                          size="sm"
                          onClick={() => setLaunching(d)}
                          disabled={!cardEnabled(d) || launchingId === d.id}
                          aria-label={t`Launch sandbox`}
                          data-testid="sandbox-launch"
                          className="h-7 shrink-0 px-2.5 text-xs"
                        >
                          {launchingId === d.id && <Loader2 className="me-1.5 h-3 w-3 animate-spin" />}
                          <Trans>Launch</Trans>
                        </Button>
                      )}
                      {/* Upgrade the FlowPad app INSIDE the box — `flow stop`,
                      `flow upgrade`, start — so a fleet is kept current without
                      anyone opening a terminal on the machines.

                      Same visibility rule as Open, and for the same reasons: a
                      box that was never launched has no VM to upgrade, and one
                      the probe cannot reach can only fail. Visible rather than
                      hover-only, unlike the icons after it: this is how a box
                      gets a fix, and an upgrade nobody can find is an upgrade
                      nobody runs. */}
                      {canUpgrade(d, details[d.id]) && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              onClick={() => setUpgrading(d)}
                              disabled={!cardEnabled(d) || upgradingIds.has(d.id)}
                              aria-label={t`Upgrade FlowPad on this sandbox`}
                              data-testid="sandbox-upgrade"
                              className="shrink-0 text-muted-foreground transition-colors hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
                            >
                              {upgradingIds.has(d.id) ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <ArrowUpCircle className="h-4 w-4" />
                              )}
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            {upgradingIds.has(d.id) ? (
                              <Trans>Upgrading — this takes a minute or two</Trans>
                            ) : (
                              <Trans>Upgrade FlowPad on this sandbox</Trans>
                            )}
                          </TooltipContent>
                        </Tooltip>
                      )}
                      <button
                        type="button"
                        onClick={() => setSharing(d)}
                        disabled={!cardEnabled(d) || !currentUser}
                        aria-label={t`Share sandbox`}
                        data-testid="sandbox-share"
                        className="text-muted-foreground opacity-0 transition-opacity hover:text-foreground disabled:pointer-events-none disabled:opacity-50 group-hover:opacity-100"
                      >
                        <UserPlus className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setEditingLogin(d)}
                        disabled={!cardEnabled(d) || !currentUser}
                        aria-label={t`Login access`}
                        title={t`Who may log in to this sandbox`}
                        data-testid="sandbox-login-access"
                        className="text-muted-foreground opacity-0 transition-opacity hover:text-foreground disabled:pointer-events-none disabled:opacity-50 group-hover:opacity-100"
                      >
                        <ShieldCheck className="h-4 w-4" />
                      </button>
                      {/* Sign THIS box out — shown only when the box is running the
                      session of the person looking at the page.

                      The condition is the point. A box holds one cloud session,
                      so "log out" is only ever meaningful about that one user;
                      offering it while the box is signed in as someone else
                      would read as a way to evict them, which this is not (it
                      would also 403 — `ops` is owner-only). Comparison is on the
                      normalized email because `logged_in_user` is whatever the
                      box's provider record carried.

                      This does NOT sign the viewer out of the hub page: the
                      credentials being cleared live on the box's disk. */}
                      {isSignedInAsMe(d, currentUser?.email) && (
                        <button
                          type="button"
                          onClick={() => void logoutSandbox(d)}
                          disabled={loggingOutIds.has(d.id) || !cardEnabled(d)}
                          aria-label={t`Sign this sandbox out`}
                          title={t`Sign out of this sandbox (you stay signed in here)`}
                          data-testid="sandbox-logout"
                          className="text-muted-foreground opacity-0 transition-opacity hover:text-foreground disabled:pointer-events-none disabled:opacity-50 group-hover:opacity-100"
                        >
                          {loggingOutIds.has(d.id) ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <LogOut className="h-4 w-4" />
                          )}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={openSandboxSecrets}
                        disabled={!cardEnabled(d)}
                        aria-label={t`Machine secrets`}
                        data-testid="sandbox-secrets"
                        className="text-muted-foreground opacity-0 transition-opacity hover:text-foreground group-hover:opacity-100"
                      >
                        <KeyRound className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => void deleteSandbox(d)}
                        disabled={deletingIds.has(d.id) || !cardEnabled(d)}
                        aria-label={t`Delete sandbox`}
                        data-testid="sandbox-delete"
                        className="text-muted-foreground opacity-0 transition-opacity hover:text-destructive disabled:pointer-events-none disabled:opacity-50 group-hover:opacity-100"
                      >
                        {deletingIds.has(d.id) ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                    <SandboxStatus
                      info={details[d.id]}
                      now={now}
                      launched={isLaunched(d)}
                      launching={launchingId === d.id}
                      loggedInUser={d.logged_in_user}
                      autoLogin={d.auto_login}
                    />
                    <NodeAgents node={d} />
                  </div>
                ))}
              </div>

              {/* Progress used to render HERE, behind a dialog that had already
              closed. It now lives inside NewSandboxDialog, which stays open for
              the whole create — so there is nothing left to show on the page. */}
            </div>
          </>
        )}
      </div>

      {/* New-sandbox modal: name + optional git repo (with the connect-GitHub gate). */}
      <AddMachineDialog
        open={addMachineCode !== null}
        initialCode={addMachineCode || null}
        onOpenChange={(o) => {
          if (!o) setAddMachineCode(null);
        }}
        onApproved={() => void refetch()}
      />
      <NewSandboxDialog
        open={!!newSandbox}
        onOpenChange={(o) => {
          if (!o) setNewSandbox(null);
        }}
        defaultName={nextSandboxName(sandboxes)}
        initialGitUrl={newSandbox?.gitUrl}
        // The sandbox opens on the project you're working on unless you change
        // it — same source the footer's StatusBar reads.
        currentProject={currentProject}
        projects={projects}
        onCreate={createSandbox}
        onLaunch={launchSandbox}
        onOpen={openSandbox}
        steps={steps}
      />

      {/* First boot of a box created earlier — the click that starts costing
          money, and the last moment auto-login can be chosen. */}
      <LaunchSandboxDialog
        open={!!launching}
        onOpenChange={(o) => {
          if (!o) setLaunching(null);
        }}
        sandbox={launching}
        onLaunch={launchSandbox}
        onOpen={openSandbox}
        steps={steps}
      />

      <ConnectMachineDialog
        where={connecting ? details[connecting.id]?.hostname || connecting.name || '' : null}
        onOpenChange={(o) => {
          if (!o) setConnecting(null);
        }}
      />

      {/* Share / hand over a sandbox. `isOwner` is a UI courtesy only — the hub
          gates both the invite and the auto-login action on ownership. */}
      <ShareSandboxDialog
        open={!!sharing}
        onOpenChange={(o) => {
          if (!o) setSharing(null);
        }}
        sandbox={sharing}
        isOwner={!!sharing}
        currentUserId={currentUser?.id}
        currentUserEmail={currentUser?.email}
        onShared={() => void refetch()}
        // Sharing a box that was never launched hands out a link the recipient
        // cannot act on, so the confirm offers the fix: the SAME dialog the card's
        // Launch button opens, with its checklist and its auto-login choice.
        // Only opens the launcher — the share dialog closes itself, as it does on
        // every other exit.
        onLaunchInstead={() => setLaunching(sharing)}
      />

      {/* Who may sign the box in from a browser. Owner-only on the hub; the
          dialog shows the hub's refusal to anyone else. */}
      <LoginAccessDialog
        open={!!editingLogin}
        onOpenChange={(o) => {
          if (!o) setEditingLogin(null);
        }}
        sandbox={editingLogin}
        onSaved={() => void refetch()}
      />

      {/* Upgrading stops the app inside the box, reinstalls it from PyPI and
          starts it again. Not destructive — nothing on the machine is lost — but
          it interrupts whoever is using it, and it wakes a paused box, so it
          asks first and says both. */}
      <ConfirmDialog
        open={!!upgrading}
        onOpenChange={(o) => {
          if (!o) setUpgrading(null);
        }}
        title={t`Upgrade FlowPad on this sandbox?`}
        description={t`FlowPad stops, upgrades to the latest release and starts again on ${upgradeTargetName}. Anyone working in it is interrupted for a minute or two, and a paused sandbox is woken to do it.`}
        confirmLabel={t`Upgrade`}
        onConfirm={() => {
          const node = upgrading;
          setUpgrading(null);
          if (node) void upgradeSandbox(node);
        }}
      />

      {/* One confirm for every bulk action: what will run, and how many are skipped. */}
      {bulkAction && (
        <BulkActionConfirm
          action={bulkAction}
          targets={targets[bulkAction]}
          skipped={selectedNodes.length - targets[bulkAction].length}
          onClose={() => setBulkAction(null)}
          onConfirm={(nodes) => {
            clearSelection();
            void runBulkAction(bulkAction, nodes);
          }}
        />
      )}

      {/* Deleting a project is not undoable and it is shared — the people it was
          shared with lose it too — so it asks first, unlike the sandbox rows. */}
    </div>
  );
}

export default HubHome;
